/**
 * Chart Defaults Configuration
 * 
 * Centralized default values for all chart types and styling.
 * This file serves as the single source of truth for:
 * - Default chart appearance
 * - Color palettes
 * - Chart-specific settings
 * - Widget container styling
 * 
 * For theming: Create a new file (e.g., chartThemes/dark.ts) with the same structure
 * and import it based on tenant/user preference.
 */

// =============================================================================
// COLOR PALETTES
// =============================================================================

export const COLOR_PALETTES = {
    // Default ECharts-inspired palette
    default: ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'],

    // Professional/Corporate
    corporate: ['#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777'],

    // Soft/Pastel
    pastel: ['#93c5fd', '#86efac', '#fde047', '#fca5a5', '#a5b4fc', '#5eead4', '#fdba74'],

    // Vibrant
    vibrant: ['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'],

    // Monochrome Blue
    monoBlue: ['#1e3a5f', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'],

    // Healthcare
    healthcare: ['#0d9488', '#0891b2', '#2563eb', '#7c3aed', '#db2777', '#ea580c'],

    // Finance
    finance: ['#047857', '#0369a1', '#4338ca', '#a21caf', '#be123c', '#b45309']
};

// =============================================================================
// GLOBAL DEFAULTS
// =============================================================================

export const GLOBAL_DEFAULTS = {
    // Legend Position
    legend: {
        show: true,
        vPosition: 'top',      // 'top' | 'middle' | 'bottom'
        hPosition: 'right',    // 'left' | 'center' | 'right' | 'custom'
        fontSize: 12,
        fontWeight: 'normal',
        fontStyle: 'normal',
        fontColor: '#64748b'   // Legend text color (slate-500)
    },

    // Plot Area (Chart Grid)
    plotArea: {
        paddingTop: 30,
        paddingBottom: 10,
        paddingLeft: 10,
        paddingRight: 10,
        backgroundColor: 'transparent',
        showBorder: false,
        borderColor: '#e2e8f0'
    },

    // Tooltip
    tooltip: {
        show: true,
        trigger: 'axis',  // 'axis' for cartesian, 'item' for pie/scatter
        backgroundColor: 'rgba(50,50,50,0.9)',
        borderColor: '#333',
        textColor: '#fff',
        fontSize: 12
    },

    // Data Labels
    dataLabel: {
        show: false,
        position: 'top',   // 'top' | 'inside' | 'outside' | 'center'
        rotate: 0,
        fontSize: 12,
        fontWeight: 'normal',
        color: '#333'
    },

    // Animation
    animation: true,

    // X-Axis
    xAxis: {
        show: true,
        showTitle: false,
        titleText: '',
        showGrid: false,
        labelRotation: 0,
        fontSize: 12,
        fontWeight: 'normal',
        labelColor: '#64748b',   // Axis label text color (slate-500)
        lineColor: '#cbd5e1',    // Axis line color (slate-300)
        gridColor: '#e2e8f0'     // Grid line color (slate-200)
    },

    // Y-Axis (same colors as X-Axis for consistency)
    yAxis: {
        show: true,
        showTitle: false,
        titleText: '',
        showGrid: true,
        logScale: false,
        fontSize: 12,
        fontWeight: 'normal',
        labelColor: '#64748b',   // Axis label text color (slate-500)
        lineColor: '#cbd5e1',    // Axis line color (slate-300)
        gridColor: '#e2e8f0'     // Grid line color (slate-200)
    }
};

// =============================================================================
// CHART-SPECIFIC DEFAULTS
// =============================================================================

export const CHART_DEFAULTS = {
    // Bar Chart
    bar: {
        horizontal: false,
        stacked: false,
        stackType: 'normal',   // 'normal' | '100%'
        barWidth: 'auto',      // 'auto' | '20%' | '40%' | '60%' | '80%'
        borderRadius: 0        // 0-10
    },

    // Line Chart
    line: {
        smooth: false,         // false | true | 'step'
        showSymbol: true,
        symbolSize: 4,
        stacked: false,
        stackType: 'normal',
        lineWidth: 2
    },

    // Area Chart
    area: {
        smooth: false,
        showSymbol: true,
        symbolSize: 4,
        opacity: 0.3,
        stacked: false,
        stackType: 'normal'
    },

    // Pie/Donut Chart
    pie: {
        innerRadius: 0,        // 0 for pie, 40+ for donut
        outerRadius: 70,
        borderRadius: 0,
        padAngle: 0,
        roseType: false,
        isHalf: false
    },

    // Donut Chart (extends pie)
    donut: {
        innerRadius: 40,
        outerRadius: 70,
        borderRadius: 0,
        padAngle: 0
    },

    // Box Plot
    boxplot: {
        horizontal: false,
        showOutliers: true,
        boxWidth: 50           // percentage
    },

    // Waterfall Chart
    waterfall: {
        horizontal: false,
        showTotal: true,
        ascending: false,
        increaseColor: '#91cc75',
        decreaseColor: '#ef4444',
        totalColor: '#5470c6'
    },

    // Scatter Chart
    scatter: {
        symbolSize: 10,
        opacity: 0.8
    },

    // Bubble Chart
    bubble: {
        maxSize: 50,
        minSize: 10,
        opacity: 0.7
    },

    // Heatmap
    heatmap: {
        minColor: '#ffffff',
        maxColor: '#5470c6',
        showLabel: true
    },

    // Map / choropleth — ECharts `series.map`; `scaleLimit` clamps roam zoom factor (wheel / pinch).
    map: {
        scaleLimit: { min: 0.82, max: 3.25 },
        roam: true,
    },

    // Gauge
    gauge: {
        min: 0,
        max: 100,
        startAngle: 225,
        endAngle: -45,
        splitNumber: 10
    },

    // Sparkline
    sparkline: {
        lineWidth: 1,
        areaOpacity: 0.1
    },

    // Treemap
    treemap: {
        borderWidth: 1,
        borderColor: '#fff'
    },

    // Sankey
    sankey: {
        nodeWidth: 20,
        nodeGap: 8,
        orient: 'horizontal'
    },

    // Funnel
    funnel: {
        sort: 'descending',
        gap: 2.5,
        orient: 'vertical'
    }
};

// =============================================================================
// TABLE CHART DEFAULTS
// =============================================================================

export const TABLE_DEFAULTS = {
    striped: true,
    showBorders: true,
    compact: false,
    enableSorting: true,
    enableSearch: false,
    defaultSortColumn: null,
    rowsPerPage: 10,
    headerBackground: '#f8fafc',
    headerColor: '#334155',
    cellPadding: 'normal'      // 'compact' | 'normal' | 'spacious'
};

// =============================================================================
// KPI CARD DEFAULTS
// =============================================================================

export const KPI_DEFAULTS = {
    valueFormat: 'compact',
    showTrend: true,
    trendPositiveColor: '#22c55e',
    trendNegativeColor: '#ef4444',
    fontSize: 32,
    labelFontSize: 14
};

// =============================================================================
// WIDGET CONTAINER DEFAULTS
// =============================================================================

export const WIDGET_DEFAULTS = {
    // Card Appearance
    backgroundColor: '#ffffff',
    borderRadius: 8,
    shadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    border: false,
    borderWidth: 1,
    borderColor: '#e2e8f0',

    // Title
    showTitle: true,
    titleFontSize: 10,
    titleFontWeight: 'bold',
    titleColor: '#334155',
    titleTextTransform: 'uppercase',

    // Content Padding (inside widget)
    contentPadding: 0,

    // Default Size (grid units)
    defaultWidth: 30,
    defaultHeight: 20
};

// =============================================================================
// GRID LAYOUT DEFAULTS
// =============================================================================

export const GRID_DEFAULTS = {
    columns: { lg: 120, md: 120, sm: 120, xs: 60, xxs: 40 },
    rowHeight: 5,
    margin: [5, 5],
    compactType: null,
    preventCollision: true
};

// =============================================================================
// EXPORT ALL AS SINGLE OBJECT (Optional - for easy import)
// =============================================================================

export const DEFAULTS = {
    colors: COLOR_PALETTES,
    global: GLOBAL_DEFAULTS,
    charts: CHART_DEFAULTS,
    table: TABLE_DEFAULTS,
    kpi: KPI_DEFAULTS,
    widget: WIDGET_DEFAULTS,
    grid: GRID_DEFAULTS
};

// Default export for convenience
export default DEFAULTS;
